import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { inventarioService, personaService, solicitudService } from '../services/inventarioService'
import { fotoService } from '../services/fotoService'
import { teletrabajoService } from '../services/teletrabajoService'
import { planillaService } from '../services/planillaService'
import { DatosBien, DatosFichaTeletrabajo, DiaHabil, Domicilio, FiltrosBienes, Sector } from '../types/inventario'

/**
 * Toda clave del inventario parte con INVENTARIO: un cambio (alta, traslado, encargado,
 * plancheta…) toca varias vistas a la vez —las dos oficinas de un traslado, sus
 * historiales, el resumen, el contador de la barra— y basta con invalidar la raíz.
 */
const INVENTARIO = 'inventario'

/** `activo`: solo para quien ve el inventario (al resto la API le responde 403). */
export function useResumen(activo = true) {
  return useQuery({ queryKey: [INVENTARIO, 'resumen'], queryFn: inventarioService.resumen, enabled: activo })
}

export function useRecintos() {
  return useQuery({ queryKey: [INVENTARIO, 'recintos'], queryFn: inventarioService.recintos })
}

export function useRecinto(codigo: string) {
  return useQuery({ queryKey: [INVENTARIO, 'recinto', codigo], queryFn: () => inventarioService.recinto(codigo) })
}

export function useBienesDeRecinto(codigo: string) {
  return useQuery({ queryKey: [INVENTARIO, 'recinto', codigo, 'bienes'], queryFn: () => inventarioService.bienesDeRecinto(codigo) })
}

export function useHistorialRecinto(codigo: string, activo = true) {
  return useQuery({
    queryKey: [INVENTARIO, 'recinto', codigo, 'historial'],
    queryFn: () => inventarioService.historialRecinto(codigo),
    enabled: activo,
  })
}

export function usePlancheta(codigo: string) {
  return useQuery({ queryKey: [INVENTARIO, 'recinto', codigo, 'plancheta'], queryFn: () => inventarioService.plancheta(codigo) })
}

export function useBienes(filtros: FiltrosBienes) {
  return useQuery({
    queryKey: [INVENTARIO, 'bienes', filtros],
    queryFn: () => inventarioService.bienes(filtros),
    placeholderData: keepPreviousData,
  })
}

export function useBien(id: number) {
  return useQuery({ queryKey: [INVENTARIO, 'bien', id], queryFn: () => inventarioService.bien(id) })
}

export function useFichaPublica(clave: string) {
  return useQuery({ queryKey: [INVENTARIO, 'ficha', clave], queryFn: () => inventarioService.fichaPublica(clave) })
}

export function useHistorialBien(id: number) {
  return useQuery({ queryKey: [INVENTARIO, 'bien', id, 'historial'], queryFn: () => inventarioService.historialBien(id) })
}

/** Búsqueda bajo demanda (revisar por N°, traer un bien): no se cachea. */
export function useBuscarBien() {
  return useMutation({ mutationFn: (q: string) => inventarioService.buscar(q) })
}

/** Una acción que cambia el inventario: al terminar, todas las vistas del inventario se recargan. */
function useCambio<A, R>(accion: (args: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: accion, onSuccess: () => qc.invalidateQueries({ queryKey: [INVENTARIO] }) })
}

export const useCrearRecinto = () =>
  useCambio((d: { codigo: string; nombre: string; sector: Sector; piso: string }) => inventarioService.crearRecinto(d))
export const useCambiarEncargado = () =>
  useCambio((d: { codigo: string; persona: number | null; motivo?: string }) => inventarioService.cambiarEncargado(d.codigo, d.persona, d.motivo))
export const useImprimirPlancheta = () => useCambio((codigo: string) => inventarioService.imprimirPlancheta(codigo))
export const useMarcarPlancheta = () =>
  useCambio((d: { codigo: string; estado: string; nota: string }) => inventarioService.marcarPlancheta(d.codigo, d.estado, d.nota))
export const useRegistrarBien = () => useCambio((d: { recinto: string; datos: DatosBien }) => inventarioService.registrarBien(d.recinto, d.datos))
export const useEditarBien = () =>
  useCambio((d: { id: number; datos: DatosBien & { motivo?: string } }) => inventarioService.editarBien(d.id, d.datos))
export const useTrasladar = () =>
  useCambio((d: { bienes: number[]; destino: string; motivo?: string }) => inventarioService.trasladar(d.bienes, d.destino, d.motivo))
export const useMarcarNoUbicado = () => useCambio((d: { id: number; motivo: string }) => inventarioService.marcarNoUbicado(d.id, d.motivo))
export const useMarcarNoUbicados = () =>
  useCambio((d: { bienes: number[]; motivo: string }) => inventarioService.marcarNoUbicados(d.bienes, d.motivo))
export const useDarDeBaja = () => useCambio((d: { bienes: number[]; motivo: string }) => inventarioService.darDeBaja(d.bienes, d.motivo))
export const useRevertirBaja = () =>
  useCambio((d: { bienes: number[]; motivo?: string }) => inventarioService.revertirBaja(d.bienes, d.motivo))

export const useMarcarEtiquetas = () =>
  useCambio((d: { bienes: number[]; pendiente: boolean }) => inventarioService.marcarEtiquetas(d.bienes, d.pendiente))

/** Trae los bienes de un filtro para imprimir sus etiquetas (no cambia nada). */
export function useParaEtiquetas() {
  return useMutation({ mutationFn: inventarioService.paraEtiquetas })
}

/** Las fotos del bien, por carpeta. `activo`: solo se piden al abrir la galería. */
export function useFotos(bien: number, activo: boolean) {
  return useQuery({ queryKey: [INVENTARIO, 'bien', bien, 'fotos'], queryFn: () => fotoService.carpetas(bien), enabled: activo })
}

export const useSubirFotos = () =>
  useCambio((d: { bien: number; archivos: File[]; observacion: string; alAvanzar?: (p: number) => void }) =>
    fotoService.subir(d.bien, d.archivos, d.observacion, d.alAvanzar),
  )
export const useDescribirFoto = () => useCambio((d: { id: number; observacion: string }) => fotoService.describir(d.id, d.observacion))
export const useEliminarFoto = () => useCambio((id: number) => fotoService.eliminar(id))

/** Los bienes marcados para dar de baja. */
export function useBajas() {
  return useQuery({ queryKey: [INVENTARIO, 'bajas'], queryFn: inventarioService.bajas })
}
export const useMarcarUbicado = () =>
  useCambio((d: { id: number; recinto: string; motivo?: string }) => inventarioService.marcarUbicado(d.id, d.recinto, d.motivo))

/** Solicitudes de traslado (`estado`: pendientes o resueltas). Aprobar traslada: recarga todo el inventario. */
export function useSolicitudes(estado?: 'PENDIENTE' | 'RESUELTAS', activo = true) {
  return useQuery({ queryKey: [INVENTARIO, 'solicitudes', estado ?? 'todas'], queryFn: () => solicitudService.lista(estado), enabled: activo })
}
export const usePedirTraslado = () =>
  useCambio((d: { bienes: number[]; destino: string; motivo: string }) => solicitudService.pedir(d.bienes, d.destino, d.motivo))
export const useAprobarSolicitud = () => useCambio((d: { id: number; respuesta?: string }) => solicitudService.aprobar(d.id, d.respuesta))
export const useRechazarSolicitud = () => useCambio((d: { id: number; respuesta: string }) => solicitudService.rechazar(d.id, d.respuesta))
export const useAnularSolicitud = () => useCambio((id: number) => solicitudService.anular(id))

export function usePersonas(q: string) {
  const texto = q.trim()
  return useQuery({ queryKey: ['personas', texto], queryFn: () => personaService.buscar(texto), enabled: texto.length >= 2 })
}

export function useAgregarPersona() {
  return useMutation({ mutationFn: personaService.agregar })
}

/** Vista previa de una planilla: no cambia nada. */
export function useRevisarPlanilla() {
  return useMutation({ mutationFn: planillaService.revisar })
}

/** Aplica una planilla (todo o nada): recarga todas las vistas del inventario. */
export const useAplicarPlanilla = () => useCambio((archivo: File) => planillaService.aplicar(archivo))

// Las descargas no cambian nada: son mutaciones solo para tener `isPending` y el error.

/** Los bienes de un filtro de la lista, como planilla para editar. */
export function useDescargarPlanilla() {
  return useMutation({ mutationFn: planillaService.descargarBienes })
}

/** La planilla para agregar bienes nuevos (cuántos de cada tipo). */
export function useDescargarPlantilla() {
  return useMutation({ mutationFn: planillaService.descargarPlantilla })
}

/** La planilla subida, con sus errores, para corregirla en el computador. */
export function useDescargarErrores() {
  return useMutation({ mutationFn: planillaService.descargarErrores })
}

// --- Teletrabajo (solo quien administra bienes) --------------------------------

export function useTeletrabajoBien(bien: number, activo = true) {
  return useQuery({ queryKey: [INVENTARIO, 'teletrabajo', 'bien', bien], queryFn: () => teletrabajoService.delBien(bien), enabled: activo })
}
export function useTeletrabajoVigentes() {
  return useQuery({ queryKey: [INVENTARIO, 'teletrabajo', 'vigentes'], queryFn: teletrabajoService.vigentes })
}
export function useGuias(semana: string) {
  return useQuery({ queryKey: [INVENTARIO, 'teletrabajo', 'guias', semana], queryFn: () => teletrabajoService.guias(semana), placeholderData: keepPreviousData })
}
export function usePersonasTeletrabajo(q: string) {
  const texto = q.trim()
  return useQuery({ queryKey: ['personas', 'teletrabajo', texto], queryFn: () => teletrabajoService.personas(texto), enabled: texto.length >= 2 })
}
export const useCrearTeletrabajo = () => useCambio((d: { bien: number; datos: DatosFichaTeletrabajo }) => teletrabajoService.crear(d.bien, d.datos))
export const useEditarTeletrabajo = () =>
  useCambio((d: { id: number; datos: Partial<Domicilio> & { dias?: DiaHabil[]; observacion?: string } }) => teletrabajoService.editar(d.id, d.datos))
export const useTerminarTeletrabajo = () => useCambio((d: { id: number; motivo?: string }) => teletrabajoService.terminar(d.id, d.motivo))
